import React, { useState, useEffect, useCallback } from 'react';

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    fontSize: 'normal',
    contrast: 'normal',
    screenReader: false,
    keyboardNav: true,
    animations: true,
    focusIndicator: true,
    highlightLinks: false,
    dyslexiaFriendly: false
  });

  // Load saved settings from localStorage
  useEffect(() => {
    const applySettings = (currentSettings) => {
      const root = document.documentElement;
      
      // Font size adjustments
      switch (currentSettings.fontSize) {
        case 'small':
          root.style.setProperty('--accessibility-font-scale', '0.9');
          break;
        case 'large':
          root.style.setProperty('--accessibility-font-scale', '1.2');
          break;
        case 'extra-large':
          root.style.setProperty('--accessibility-font-scale', '1.4');
          break;
        default:
          root.style.setProperty('--accessibility-font-scale', '1');
      }

      // Contrast adjustments
      switch (currentSettings.contrast) {
        case 'high':
          root.classList.add('high-contrast');
          root.classList.remove('dark-mode');
          break;
        case 'dark':
          root.classList.add('dark-mode');
          root.classList.remove('high-contrast');
          break;
        default:
          root.classList.remove('high-contrast', 'dark-mode');
      }

      // Animation controls
      if (!currentSettings.animations) {
        root.classList.add('reduce-motion');
      } else {
        root.classList.remove('reduce-motion');
      }

      // Focus indicator
      if (currentSettings.focusIndicator) {
        root.classList.add('enhanced-focus');
      } else {
        root.classList.remove('enhanced-focus');
      }

      // Highlight links
      if (currentSettings.highlightLinks) {
        root.classList.add('highlight-links');
      } else {
        root.classList.remove('highlight-links');
      }

      // Dyslexia friendly font
      if (currentSettings.dyslexiaFriendly) {
        root.classList.add('dyslexia-friendly');
      } else {
        root.classList.remove('dyslexia-friendly');
      }

      // Screen reader announcements
      if (currentSettings.screenReader) {
        announceToScreenReader('Accessibility settings loaded', true);
      }
    };

    const savedSettings = localStorage.getItem('accessibility-settings');
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      setSettings(parsed);
      applySettings(parsed);
    }
  }, []); // No dependencies needed

  // Save settings to localStorage and apply them
  const updateSetting = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    localStorage.setItem('accessibility-settings', JSON.stringify(newSettings));
    
    // Apply settings inline
    const root = document.documentElement;
    
    // Font size adjustments
    switch (newSettings.fontSize) {
      case 'small':
        root.style.setProperty('--accessibility-font-scale', '0.9');
        break;
      case 'large':
        root.style.setProperty('--accessibility-font-scale', '1.2');
        break;
      case 'extra-large':
        root.style.setProperty('--accessibility-font-scale', '1.4');
        break;
      default:
        root.style.setProperty('--accessibility-font-scale', '1');
    }

    // Contrast adjustments
    switch (newSettings.contrast) {
      case 'high':
        root.classList.add('high-contrast');
        root.classList.remove('dark-mode');
        break;
      case 'dark':
        root.classList.add('dark-mode');
        root.classList.remove('high-contrast');
        break;
      default:
        root.classList.remove('high-contrast', 'dark-mode');
    }

    // Animation controls
    if (!newSettings.animations) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }

    // Focus indicator
    if (newSettings.focusIndicator) {
      root.classList.add('enhanced-focus');
    } else {
      root.classList.remove('enhanced-focus');
    }

    // Highlight links
    if (newSettings.highlightLinks) {
      root.classList.add('highlight-links');
    } else {
      root.classList.remove('highlight-links');
    }

    // Dyslexia friendly font
    if (newSettings.dyslexiaFriendly) {
      root.classList.add('dyslexia-friendly');
    } else {
      root.classList.remove('dyslexia-friendly');
    }

    // Announce specific changes with audio
    if (newSettings.screenReader) {
      let message = '';
      switch (key) {
        case 'fontSize':
          message = `Font size changed to ${value}`;
          break;
        case 'contrast':
          message = `Contrast mode changed to ${value}`;
          break;
        case 'highlightLinks':
          message = value ? 'Link highlighting enabled' : 'Link highlighting disabled';
          break;
        case 'dyslexiaFriendly':
          message = value ? 'Dyslexia friendly font enabled' : 'Dyslexia friendly font disabled';
          break;
        case 'animations':
          message = value ? 'Animations enabled' : 'Animations disabled';
          break;
        case 'focusIndicator':
          message = value ? 'Enhanced focus indicators enabled' : 'Enhanced focus indicators disabled';
          break;
        case 'keyboardNav':
          message = value ? 'Keyboard navigation enabled' : 'Keyboard navigation disabled';
          break;
        case 'screenReader':
          message = value ? 'Audio announcements enabled' : 'Audio announcements disabled';
          break;
        default:
          message = 'Setting updated';
      }
      announceToScreenReader(message, true);
    }
  };

  // Load voices when component mounts for better TTS
  useEffect(() => {
    const loadVoices = () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.getVoices();
      }
    };

    loadVoices();
    
    if ('speechSynthesis' in window) {
      window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
      return () => {
        window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      };
    }
  }, []);

  // Announce messages to screen readers and optionally speak them
  const announceToScreenReader = useCallback((message, speak = false) => {
    // Screen reader announcement
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      if (document.body.contains(announcement)) {
        document.body.removeChild(announcement);
      }
    }, 1000);

    // Optional text-to-speech with American accent
    if (speak && 'speechSynthesis' in window) {
      try {
        // Cancel any ongoing speech
        window.speechSynthesis.cancel();
        
        const utterance = new SpeechSynthesisUtterance(message);
        utterance.rate = 0.9;
        utterance.pitch = 1;
        utterance.volume = 0.8;
        
        // Try to set American English voice
        const voices = window.speechSynthesis.getVoices();
        const americanVoice = voices.find(voice => 
          voice.lang === 'en-US' || 
          voice.name.includes('US') || 
          voice.name.includes('American') ||
          voice.name.includes('United States')
        );
        
        if (americanVoice) {
          utterance.voice = americanVoice;
        } else {
          // Fallback to any English voice
          const englishVoice = voices.find(voice => voice.lang.startsWith('en'));
          if (englishVoice) {
            utterance.voice = englishVoice;
          }
        }
        
        utterance.lang = 'en-US';
        window.speechSynthesis.speak(utterance);
      } catch (error) {
        console.log('Text-to-speech not available:', error);
      }
    }
  }, []); // Remove dependency on settings.screenReader

  // Reset all settings to default
  const resetSettings = () => {
    const defaultSettings = {
      fontSize: 'normal',
      contrast: 'normal',
      screenReader: false,
      keyboardNav: true,
      animations: true,
      focusIndicator: true,
      highlightLinks: false,
      dyslexiaFriendly: false
    };
    setSettings(defaultSettings);
    localStorage.removeItem('accessibility-settings');
    
    // Apply default settings
    const root = document.documentElement;
    root.style.setProperty('--accessibility-font-scale', '1');
    root.classList.remove('high-contrast', 'dark-mode', 'reduce-motion', 'enhanced-focus', 'highlight-links', 'dyslexia-friendly');
    
    announceToScreenReader('Accessibility settings reset to default', true);
  };

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Alt + A to toggle accessibility widget
      if (e.altKey && e.key === 'a') {
        e.preventDefault();
        setIsOpen(!isOpen);
        if (settings.screenReader) {
          announceToScreenReader(isOpen ? 'Accessibility menu closed' : 'Accessibility menu opened', true);
        }
      }
      
      // Escape to close widget
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        if (settings.screenReader) {
          announceToScreenReader('Accessibility menu closed', true);
        }
      }
    };

    if (settings.keyboardNav) {
      document.addEventListener('keydown', handleKeyPress);
      return () => document.removeEventListener('keydown', handleKeyPress);
    }
  }, [isOpen, settings.keyboardNav, settings.screenReader]); // Remove announceToScreenReader dependency

  return (
    <>
      {/* Accessibility Button */}
      <button
        className="accessibility-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open accessibility options"
        aria-expanded={isOpen}
        aria-controls="accessibility-panel"
        title="Accessibility Options (Alt + A)"
      >
        <i className="bi bi-universal-access" aria-hidden="true"></i>
        <span className="sr-only">Accessibility</span>
      </button>

      {/* Accessibility Panel */}
      {isOpen && (
        <div
          id="accessibility-panel"
          className="accessibility-panel"
          role="dialog"
          aria-labelledby="accessibility-title"
          aria-modal="false"
        >
          <div className="accessibility-header">
            <h3 id="accessibility-title">
              <i className="bi bi-universal-access me-2" aria-hidden="true"></i>
              Accessibility Options
            </h3>
            <button
              className="btn-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close accessibility options"
            >
              <i className="bi bi-x" aria-hidden="true"></i>
            </button>
          </div>

          <div className="accessibility-content">
            {/* Font Size */}
            <div className="accessibility-group">
              <label className="accessibility-label">
                <i className="bi bi-fonts me-2" aria-hidden="true"></i>
                Text Size
              </label>
              <div className="btn-group" role="group" aria-label="Font size options">
                {['small', 'normal', 'large', 'extra-large'].map((size) => (
                  <button
                    key={size}
                    className={`btn btn-outline-primary ${settings.fontSize === size ? 'active' : ''}`}
                    onClick={() => updateSetting('fontSize', size)}
                    aria-pressed={settings.fontSize === size}
                  >
                    {size === 'extra-large' ? 'XL' : size.charAt(0).toUpperCase() + size.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Contrast */}
            <div className="accessibility-group">
              <label className="accessibility-label">
                <i className="bi bi-contrast me-2" aria-hidden="true"></i>
                Contrast & Theme
              </label>
              <div className="btn-group" role="group" aria-label="Contrast options">
                {[
                  { key: 'normal', label: 'Normal' },
                  { key: 'high', label: 'High Contrast' },
                  { key: 'dark', label: 'Dark Mode' }
                ].map((option) => (
                  <button
                    key={option.key}
                    className={`btn btn-outline-primary ${settings.contrast === option.key ? 'active' : ''}`}
                    onClick={() => updateSetting('contrast', option.key)}
                    aria-pressed={settings.contrast === option.key}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Toggle Options */}
            <div className="accessibility-group">
              <label className="accessibility-label">
                <i className="bi bi-toggles me-2" aria-hidden="true"></i>
                Features
              </label>
              
              <div className="accessibility-toggles">
                <label className="accessibility-toggle-item">
                  <input
                    type="checkbox"
                    checked={settings.screenReader}
                    onChange={(e) => updateSetting('screenReader', e.target.checked)}
                    aria-describedby="screen-reader-desc"
                  />
                  <span>Screen Reader & Audio Announcements</span>
                  <small id="screen-reader-desc" className="text-muted">
                    Enable audio feedback and screen reader announcements for actions
                  </small>
                </label>

                <label className="accessibility-toggle-item">
                  <input
                    type="checkbox"
                    checked={settings.keyboardNav}
                    onChange={(e) => updateSetting('keyboardNav', e.target.checked)}
                    aria-describedby="keyboard-desc"
                  />
                  <span>Enhanced Keyboard Navigation</span>
                  <small id="keyboard-desc" className="text-muted">
                    Use Alt+A to toggle this menu
                  </small>
                </label>

                <label className="accessibility-toggle-item">
                  <input
                    type="checkbox"
                    checked={settings.animations}
                    onChange={(e) => updateSetting('animations', e.target.checked)}
                    aria-describedby="animations-desc"
                  />
                  <span>Enable Animations</span>
                  <small id="animations-desc" className="text-muted">
                    Disable if you prefer reduced motion
                  </small>
                </label>

                <label className="accessibility-toggle-item">
                  <input
                    type="checkbox"
                    checked={settings.focusIndicator}
                    onChange={(e) => updateSetting('focusIndicator', e.target.checked)}
                    aria-describedby="focus-desc"
                  />
                  <span>Enhanced Focus Indicators</span>
                  <small id="focus-desc" className="text-muted">
                    Stronger visual focus outlines
                  </small>
                </label>

                <label className="accessibility-toggle-item">
                  <input
                    type="checkbox"
                    checked={settings.highlightLinks}
                    onChange={(e) => updateSetting('highlightLinks', e.target.checked)}
                    aria-describedby="highlight-desc"
                  />
                  <span>Highlight Links</span>
                  <small id="highlight-desc" className="text-muted">
                    Make all links more visible with enhanced styling
                  </small>
                </label>

                <label className="accessibility-toggle-item">
                  <input
                    type="checkbox"
                    checked={settings.dyslexiaFriendly}
                    onChange={(e) => updateSetting('dyslexiaFriendly', e.target.checked)}
                    aria-describedby="dyslexia-desc"
                  />
                  <span>Dyslexia Friendly Font</span>
                  <small id="dyslexia-desc" className="text-muted">
                    Use OpenDyslexic font for better readability
                  </small>
                </label>
              </div>
            </div>

            {/* Reset Button */}
            <div className="accessibility-group">
              <button
                className="btn btn-outline-secondary w-100 mb-2"
                onClick={resetSettings}
              >
                <i className="bi bi-arrow-clockwise me-2" aria-hidden="true"></i>
                Reset to Default
              </button>
              
              {/* Test Audio Button */}
              {settings.screenReader && (
                <button
                  className="btn btn-outline-info w-100"
                  onClick={() => announceToScreenReader('Audio test: This is a test of the text-to-speech feature', true)}
                >
                  <i className="bi bi-volume-up me-2" aria-hidden="true"></i>
                  Test Audio
                </button>
              )}
            </div>

            {/* Keyboard Shortcuts Info */}
            <div className="accessibility-group">
              <details className="accessibility-shortcuts">
                <summary>
                  <i className="bi bi-keyboard me-2" aria-hidden="true"></i>
                  Keyboard Shortcuts
                </summary>
                <ul className="shortcuts-list">
                  <li><kbd>Alt + A</kbd> - Toggle accessibility menu</li>
                  <li><kbd>Tab</kbd> - Navigate between elements</li>
                  <li><kbd>Enter/Space</kbd> - Activate buttons and links</li>
                  <li><kbd>Escape</kbd> - Close menus and modals</li>
                  <li><kbd>Arrow Keys</kbd> - Navigate within menus</li>
                </ul>
              </details>
            </div>
          </div>
        </div>
      )}

      {/* Screen reader only content */}
      <div className="sr-only" aria-live="polite" id="accessibility-announcements"></div>
    </>
  );
};

export default AccessibilityWidget;